<template>
<div>
    <div class="sm:flex flex-wrap lg:gap-x-12 justify-between">
        <div class="sm:w-1/2 md:w-1/3 xl:w-1/4">
            <div class="flex items-center mb-2">
                <jet-label for="id" value="Order #:" class="flex w-32 mb-0 leading-7"/>
                <div class="flex mb-1">
                    <Link v-if="isCustomer" :href="route('customer_orders.show', order.id)">{{ order.order_number }}</Link>
                    <Link v-else :href="route('orders.show', order.id)">{{ order.order_number }}</Link>
                </div>
            </div>

            <div v-if="showPlacedByStamp" class="flex items-center mb-2">
                <jet-label for="placed_by" value="Placed By:" class="flex w-32 mb-0 leading-7"/>
                <div class="flex mb-1">
                    {{ order.user?.name }}
                </div>
            </div>

            <div
                v-if="order.recurring_orders && order.recurring_orders.length"
                class="flex items-start mb-2"
                >
                <jet-label for="recurring_orders" value="Recurring Order #:" class="flex w-32 mb-0 leading-7 self-start"/>
                <div class="flex flex-wrap items-center gap-x-1 text-sm text-gray-700 mb-1 ml-2 pl-0.5 py-0.5">
                    <span
                        v-for="(rOrder, index) in order.recurring_orders"
                        :key="rOrder.id"
                        class="inline-flex items-center"
                    >
                    <Link :href="route('recurring_orders.show', rOrder.id)" class="text-blue-600 hover:underline text-base">
                        {{ rOrder.id }}
                    </Link>
                    <span v-if="index < order.recurring_orders.length - 1">,</span>
                    </span>
                </div>
            </div>

            <div v-if="order.created_by_refund_id" class="flex items-center mb-2">
                <jet-label for="created_by_refund_id" value="Refund #:" class="flex w-32 mb-0 leading-7"/>
                <div class="flex mb-1">
                    <Link v-if="isCustomer" :href="route('customer_refunds.show', order.created_by_refund_id)">{{ order.created_by_refund_id }}</Link>
                    <Link v-else :href="route('refunds.show', order.created_by_refund_id)">{{ order.created_by_refund_id }}</Link>
                </div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="order_date" value="Order Date:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex items-end whitespace-nowrap mb-1">
                    {{ formatDate(order.order_date) }}&nbsp;&nbsp;
                    <span class="text-gray-400 text-sm">
                        {{ formatTime(order.created_at) }}
                    </span>
                </div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="ship_date" value="Ship Date:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex items-end whitespace-nowrap mb-1">
                    {{ formatDate(order.ship_date) }}&nbsp;&nbsp;
                    <span v-if="!order.is_wholesale" class="text-gray-400 text-sm">
                        {{ order.shipping_method === 'pickup' ? order.pickup_time : order.shipping_time }}
                    </span>
                </div>
            </div>

            <div v-if="!order.is_wholesale" class="flex items-center mb-2">
                <jet-label for="ship_date" value="Invoiced on:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div v-if="order.delivered_at" class="flex items-end whitespace-nowrap mb-1">
                    {{ formatTimestamp(order.delivered_at) }}&nbsp;&nbsp;
                    <span class="text-gray-400 text-sm">
                        {{ formatTime(order.delivered_at) }}
                    </span>
                </div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="shipping_method" value="Shipping Method:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex whitespace-nowrap capitalize mb-1">
                    {{ order.shipping_method }}
                </div>
            </div>

            <div class="flex items-center">
                <jet-label for="status" value="Status:" class="flex w-32 mb-0 leading-7 self-start" />
                <div class="flex flex-col mb-1">
                    <div>{{ status }}</div>
                    <div
                        v-if="showAdminCancellationReason"
                        class="text-sm text-gray-700 mt-1 whitespace-pre-wrap"
                    >
                        {{ order.cancellation_reason }}
                    </div>
                    <div v-if="order.invoice_emailed_at" class="text-xs italic">
                        Emailed on {{ formatTimestamp(order.invoice_emailed_at) }}
                    </div>
                </div>
            </div>

            <div v-if="showCancellationAudit" class="flex items-center mb-2">
                <jet-label value="Cancelled:" class="flex w-32 mb-0 leading-7 whitespace-nowrap self-start" />
                <div class="flex flex-wrap items-end gap-x-2 mb-1">
                    <span v-if="order.cancelled_by_user">{{ order.cancelled_by_user.name }}</span>
                    <span class="text-gray-400 text-sm whitespace-nowrap">
                        {{ cancellationAuditTimestamp }}
                    </span>
                </div>
            </div>

            <div v-if="order.payment_terms_def" class="flex items-center mb-2">
                <jet-label for="payment_terms" value="Terms:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex items-end whitespace-nowrap mb-1">
                    {{ order.payment_terms_def }}
                </div>
            </div>

            <div v-if="order.payment_due" class="flex items-center mb-2">
                <jet-label for="payment_due" value="Payment Due:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex items-end whitespace-nowrap mb-1">
                    {{ formatDate(order.payment_due) }}
                </div>
            </div>
        </div>

        <div class="sm:w-1/2 md:w-1/3 xl:w-1/4">
            <div class="flex items-center mb-2">
                <jet-label for="customer" value="Customer:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7 self-start" />
                <div class="flex mb-1">
                    {{ order.customer?.name || order.shipping_first_name + ' ' + order.shipping_last_name }}
                </div>
            </div>
            <div class="flex items-start mb-2">
                <jet-label for="address" value="Address:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                <div class="leading-tight mb-1">
                    <template v-if="order.customer">
                        <div>{{ order.customer.address_1 }}</div>
                        <div v-show="order.customer.address_2">{{ order.customer.address_2 }}</div>
                        <div>{{ order.customer.city }} {{ order.customer.province }}, {{ order.customer.postal_code }}</div>
                    </template>

                    <template v-else>
                        <div>{{ order.shipping_address_1 }}</div>
                        <div v-show="order.shipping_address_2">{{ order.shipping_address_2 }}</div>
                        <div>{{ order.shipping_city }} {{ order.shipping_province }}, {{ order.shipping_postal }}</div>
                    </template>
                </div>
            </div>

            <div v-if=" order.po_num" class="flex items-center mb-2">
                <jet-label value="PO #:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                <div class="flex mb-1">
                    <div>{{ order.po_num }}</div>
                </div>
            </div>

            <template v-if="isAdmin || isLocation">
                <div class="flex items-center mb-2">
                    <jet-label for="phone" value="Phone:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                    <div class="flex mb-1">
                        {{ order.customer?.phone || order.shipping_phone }}
                    </div>
                </div>

                <div class="flex items-start mb-2" v-if="order.customer?.emails.length || order.shipping_email">
                    <jet-label for="email" value="Email:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                    <div class="flex flex-1 mb-1">
                        <ul v-if="order.customer" class="leading-tight">
                            <li v-for="email in order.customer.emails" :key="email" class="break-all">{{ email }}</li>
                        </ul>
                        <template v-else>
                            {{ order.shipping_email }}
                        </template>
                    </div>
                </div>

                <div v-if="!order.is_wholesale && order.shipping_method === 'delivery'" class="flex items-center">
                    <jet-label for="delivered_image" value="Delivered Photo:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7" />
                    <div v-if="order.delivery_run_id" class="flex flex-col mb-1">
                        <div v-if="order.delivered_image" class="flex items-start">
                            <img style="width: 100px;" class="cursor-pointer"
                                :src="'/storage/' + order.delivered_image"
                                @click="previewPhotoModal = true"
                            />
                            <div class="p-2 ml-2 border rounded bg-white cursor-pointer"
                                style="top: 15px; right: 15px; line-height: 1;"
                                @click="deletePhotoModal = true"
                            >
                                <font-awesome-icon :icon="['far', 'trash-can']" class="h-5"/>
                            </div>
                        </div>
                        <font-awesome-icon v-else
                            :icon="['fas', 'camera']"
                            class="h-16 w-16 p-4 border-2 rounded cursor-pointer"
                            @click="openFileInput"
                        />
                        <jet-input-error class="mt-1" :message="imageError" />
                        <input hidden type="file" ref="fileInput" accept="image/*" @change="inputImage"/>
                    </div>
                </div>
            </template>
        </div>

        <div class="sm:w-1/2 md:w-1/3">
            <div v-if="order.payment_method_def" class="flex items-center mb-2">
                <jet-label for="payment_method" value="Payment Method:" class="flex w-32 mb-0 leading-7 self-start" />
                <div class="flex mb-1">
                    {{ order.payment_method_def }}
                </div>
            </div>
            
            <div v-if="isAdmin && order.customer" class="flex items-start mb-2">
                <jet-label for="internal_notes" value="Internal Notes:" class="flex w-32 xl:w-28 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1">
                    {{ order.customer.internal_notes }}
                </div>
            </div>

            <div class="flex items-start">
                <jet-label for="notes" value="Shipping Notes:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1" style="min-height: 28px; padding-top: 3px;">
                    {{ order.notes }}
                </div>
            </div>
            <div v-if="isAdmin && order.customer" class="flex items-start">
                <jet-label for="load_notes" value="Load Sheet Notes:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1" style="min-height: 28px; padding-top: 3px;">
                    {{ order.customer.load_notes }}
                </div>
            </div>
            <div v-if="isAdmin && order.picking_notes && order.picking_notes.trim()" class="flex items-start">
                <jet-label for="picking_notes" value="Picking Notes:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1 whitespace-pre-wrap" style="min-height: 28px; padding-top: 3px;">
                    {{ order.picking_notes }}
                </div>
            </div>

            <div v-if="isAdmin && order.skills && order.skills.length" class="flex items-start">
                <jet-label for="skills" value="Skills:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1">{{ order.skills.join(', ') }}</div>
            </div>
            <div v-if="isAdmin && order.vehicle_features && order.vehicle_features.length" class="flex items-start">
                <jet-label for="vehicle_features" value="Vehicle Type:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1">{{ order.vehicle_features.join(', ') }}</div>
            </div>
            <div v-if="isAdmin && order.priority" class="flex items-start">
                <jet-label for="priority" value="Priority:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1">{{ order.priority }}</div>
            </div>
            <div v-if="isAdmin && order.optimoroute_error" class="flex items-start">
                <jet-label for="optimoroute_error" value="OptimoRoute Error:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1 text-red-600">{{ order.optimoroute_error }}</div>
            </div>
            
            <div v-if="order.payment_method == 'credit_card'" class="flex items-start">
                <jet-label for="payment_status" value="Payment Status:" class="flex w-32 mb-0 leading-7 whitespace-nowrap" />
                <div class="flex flex-1 leading-snug mb-1" style="min-height: 28px; padding-top: 3px;">
                    {{ order.payment_status[0].toUpperCase() + order.payment_status.slice(1) }}
                </div>
            </div>
        </div>
    </div>

    <!-- Refunds -->
    <div
        v-if="isAdmin && order.refunds?.length > 0"
        class="mt-4 w-full md:w-3/4"
    >
        <span class="font-bold">Refunds</span>
        <div class="rounded-t overflow-hidden">
            <table class="w-full table">
                <thead>
                    <tr class="align-bottom bg-gray-light border">
                        <th class="text-left">#</th>
                        <th class="text-left">Status</th>
                        <th class="text-left">Submitted Date</th>
                        <th class="text-left">Review Date</th>
                        <th class="text-left">Reviewed By</th>
                        <th class="text-left">Refund Method</th>
                        <th class="text-right">Refund Amount</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="refund in order.refunds" :key="refund.id"
                        class="cursor-pointer"
                        @click="$inertia.visit(`/refunds/${refund.id}`)"
                    >
                        <td class="text-left">{{ refund.id }}</td>
                        <td class="text-left">{{ refund.statusDef }}</td>
                        <td class="text-left">{{ formatDate(refund.created_at) }}</td>
                        <td class="text-left">{{ refund.reviewed_date ? formatTimestamp(refund.reviewed_date) : null }}</td>
                        <td class="text-left">{{ refund.reviewed_by?.name }}</td>
                        <td class="text-left">{{ refund.refund_method }}</td>
                        <td class="text-right">${{ refund.amount }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Delete photo modal -->
    <jet-confirmation-modal :show="deletePhotoModal !== false" @close="deletePhotoModal = false">
        <template #title>
            Delete photo
        </template>

        <template #content>
            Are you sure you would like to delete?
        </template>

        <template #footer>
            <jet-danger-button @click="removeImage">
                Yes
            </jet-danger-button>

            <jet-tertiary-button class="ml-2" @click="deletePhotoModal = false">
                No
            </jet-tertiary-button>
        </template>
    </jet-confirmation-modal>

    <!-- Preview photo modal -->
    <jet-dialog-modal :show="previewPhotoModal !== false" @close="previewPhotoModal = false">
        <template #content>
            <img :src="'/storage/' + order.delivered_image" style="width: 800px">
        </template>

        <template #footer>
            <jet-tertiary-button class="ml-2" @click="previewPhotoModal = false">
                Close
            </jet-tertiary-button>
        </template>
    </jet-dialog-modal>
</div>
</template>

<script>
import { usePage, Link } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import JetLabel from '@/Jetstream/Label';
import JetInputError from '@/Jetstream/InputError';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import Compressor from 'compressorjs';
import { DateTime } from "luxon";

export default {
    setup () {
        const { isAdmin, isCustomer, isLocation } = UseUserAuth(usePage().props.value.user);

        return {
            isAdmin,
            isCustomer,
            isLocation,
        }
    },

    components: {
        Link,
        JetLabel,
        JetInputError,
        JetDangerButton,
        JetTertiaryButton,
        JetDialogModal,
        JetConfirmationModal,
    },

    data() {
        return {
            imageError: null,
            deletePhotoModal: false,
            previewPhotoModal: false,
        }
    },

    props: {
        order: {
            type: Object,
            required: true,
        },
        showPlacedByStamp: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        status () {
            if (this.order.status === -1 && this.isCustomer) {
                return 'Cancelled';
            }

            if (this.order.status === -1 && this.isAdmin && this.order.cancelled_by_customer) {
                return 'Cancelled by Customer';
            }

            return this.order.statusDef;
        },

        showAdminCancellationReason () {
            if (!this.isAdmin || this.order.status !== -1 || !this.order.cancellation_reason) {
                return false;
            }

            return String(this.order.cancellation_reason).trim() !== '';
        },

        showCancellationAudit () {
            if (!this.isAdmin || this.order.status !== -1 || !this.order.cancelled_at) {
                return false;
            }

            return true;
        },

        cancellationAuditTimestamp () {
            if (!this.order.cancelled_at) {
                return '';
            }

            let date = DateTime.fromSQL(this.order.cancelled_at);

            if (!date.isValid) {
                date = DateTime.fromISO(this.order.cancelled_at);
            }

            if (!date.isValid) {
                return '';
            }

            const datePart = date.toFormat('MMM d, yyyy');
            const timePart = this.formatTime(this.order.cancelled_at);

            if (timePart) {
                return `${datePart} ${timePart}`;
            }

            return datePart;
        },
    },

    methods: {
        formatDate (date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        formatTimestamp (timestamp) {
            return DateTime.fromSQL(timestamp).toFormat('MMM d, yyyy');
        },

        formatTime(timestamp) {
            let date = DateTime.fromSQL(timestamp).invalid ? DateTime.fromISO(timestamp) : DateTime.fromSQL(timestamp);

            if (date.c.hour === 0 && date.c.minute === 0 && date.c.second === 0) {
                return '';
            }

            return date.toFormat('h:mm a');
        },

        openFileInput() {
            this.$refs.fileInput.click();
        },

        inputImage(event) {
            let image = event.target.files[0];

            if (['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(image.type)) {
                const imageURL = URL.createObjectURL(image);
                this.compressFile(image);
                event.target.value = null;
                this.imageError = null;
            } else {
                this.imageError = "The image must be a file of type: jpeg, jpg, png, gif.";
            }
        },

        compressFile(file) {
            new Compressor(file, {
                checkOrientation: true,
                maxWidth: 800,
                maxHeight: 800,
                quality: 0.8,

                success: (result) => {
                    let newFile = new File([result], file.name, { type: file.type, lastModified: new Date().getTime() });

                    let formData = new FormData();
                    formData.append('image', newFile);
                    this.uploadPhoto(formData);
                },

                error: (error) => {
                    console.log(error);
                },
            });
        },

        removeImage() {
            this.uploadPhoto();
        },

        uploadPhoto(formData) {
            axios.post(route('delivery.order.upload.photo', this.order.id), formData)
                .then(({data}) => {
                    this.order.delivered_image = data.filename;
                    this.deletePhotoModal = false;
                });
        },
    }
}
</script>

<style scoped>

</style>